/**
 * SocketContext (Solid.js)
 * Socket.io Context für Real-time Updates
 */

import { createContext, useContext, createSignal, createEffect } from 'solid-js';
import type { JSX } from 'solid-js';
import { io, type Socket } from 'socket.io-client';
import { apiConfig } from '../config/api';
import { useAuth } from './AuthContext';
import { storage } from '../utils/storage';

type SocketType = Socket;

interface SocketContextType {
  socket: SocketType | null;
  isConnected: boolean;
  subscribeToColony: (colonyId: string) => void;
  unsubscribeFromColony: (colonyId: string) => void;
  subscribeToColonyList: () => void;
  unsubscribeFromColonyList: () => void;
}

const SocketContext = createContext<SocketContextType>();

export const useSocket = (): SocketContextType => {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useSocket muss innerhalb eines SocketProvider verwendet werden');
  return ctx;
};

const subscribedColonies = new Set<string>();
let isSubscribedToList = false;

export function SocketProvider(props: { children: JSX.Element }) {
  const auth = useAuth();
  const [socket, setSocket] = createSignal<SocketType | null>(null);
  const [isConnected, setIsConnected] = createSignal(false);

  createEffect(() => {
    const authed = auth.isAuthenticated;
    const user = auth.user;
    if (!authed || !user) {
      const s = socket();
      if (s) {
        s.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      subscribedColonies.clear();
      isSubscribedToList = false;
      return;
    }

    const socketURL = apiConfig.socketURL || window.location.origin;
    const newSocket = io(socketURL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
      auth: { token: storage.getItem('token') || undefined },
    }) as SocketType;

    newSocket.on('connect', () => setIsConnected(true));
    newSocket.on('disconnect', () => setIsConnected(false));
    newSocket.on('gameState:invalidated', () => {
      window.dispatchEvent(new CustomEvent('gameState:invalidated'));
    });
    newSocket.on('user:updated', (data: { userId: string; money?: number; points?: number }) => {
      const currentUser = auth.user;
      if (currentUser && data.userId === currentUser.id) {
        const update: Partial<typeof currentUser> = {};
        if (data.money !== undefined) update.money = data.money;
        if (data.points !== undefined) update.points = data.points;
        if (Object.keys(update).length > 0) {
          auth.updateUser(update);
        }
      }
    });

    setSocket(newSocket);

    return () => {
      subscribedColonies.clear();
      isSubscribedToList = false;
      newSocket.disconnect();
    };
  });

  const subscribeToColony = (colonyId: string) => {
    const s = socket();
    if (!s || !isConnected() || subscribedColonies.has(colonyId)) return;
    s.emit('colony:subscribe', colonyId);
    subscribedColonies.add(colonyId);
  };

  const unsubscribeFromColony = (colonyId: string) => {
    const s = socket();
    if (!s || !subscribedColonies.has(colonyId)) return;
    s.emit('colony:unsubscribe', colonyId);
    subscribedColonies.delete(colonyId);
  };

  const subscribeToColonyList = () => {
    const s = socket();
    if (!s || !isConnected() || isSubscribedToList) return;
    s.emit('colony:list:subscribe');
    isSubscribedToList = true;
  };

  const unsubscribeFromColonyList = () => {
    const s = socket();
    if (!s || !isSubscribedToList) return;
    s.emit('colony:list:unsubscribe');
    isSubscribedToList = false;
  };

  const value: SocketContextType = {
    get socket() { return socket(); },
    get isConnected() { return isConnected(); },
    subscribeToColony,
    unsubscribeFromColony,
    subscribeToColonyList,
    unsubscribeFromColonyList,
  };

  return (
    <SocketContext.Provider value={value}>
      {props.children}
    </SocketContext.Provider>
  );
}
